import { Component, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';

import { Subject } from 'rxjs';
import { filter, finalize, takeUntil } from 'rxjs/operators';
import { AJSTriageService } from 'src/admin-front/ajs-upgraded-providers';
import { AngularMigrationHelperService } from 'src/ajs-app/services/angularMigrationHelper.service';
import { SelectTagComponent } from 'src/app/shared/components/select-tags/select-tag/select-tag.component';
import { TaskService } from 'src/app/shared/services/task.service';
import { ModalDismissRoleEnum } from 'src/app/task-process/answer-editor/send-and-popover/modal-dismiss-role.enum';
import { environment } from 'src/environments/environment';
import { AlcmeonContextService } from '../../../core/alcmeon-context/alcmeon-context.service';
import { Site, TagGroup, TaskState } from '../../../shared/models/misc.model';
import { BasicService } from '../../../shared/services/basic.service';
import { taskIdFromTaskOrActionId } from '../../shared/id-conversions';
import { AnyTaskOrActionType, ModifiedAction, TagAction, Task, TaskOrActionType } from '../../shared/model';
import { TagsContext } from '../../shared/tags-context.class';
import { TaskProcessContext } from '../../shared/task-process-context.class';
import { TaskProcessModalsService } from '../../shared/task-process-modals.service';
import { TaskProcessUpdatesService } from '../../shared/task-process-updates.service';
import { TaskProcessService } from '../../shared/task-process.service';
import { ConfirmStealComponent } from './confirm-steal/confirm-steal.component';
import { TaskActionsResponse } from './model';
import { TaskActionsService } from './task-actions-log.service';

@Component({
  selector: 'app-task-actions-log',
  templateUrl: './task-actions-log.component.html',
  styleUrls: ['./task-actions-log.component.scss'],
})
export class TaskActionsLogComponent implements OnInit, OnDestroy {
  @Input() task: Task;
  @Input() taskProcessContext: TaskProcessContext;

  @ViewChild('selectRoutingTag') selectRoutingTag: SelectTagComponent;

  actions: TagAction[];
  receivedMsgLogEntry: any; // The very first log entry of the received message
  site: Site;
  externalUrl: string;
  tagsContext: TagsContext;
  routingTagGroup: TagGroup;
  isCRMMode: boolean;
  isProcessedTask: boolean;
  isProcessThisMessageButtonDisabled: boolean;
  isUserReadOnly: boolean;

  private taskId: number;
  private ngOnDestroy$ = new Subject();

  constructor(
    private basicService: BasicService,
    private taskActionsService: TaskActionsService,
    private alcmeonContext: AlcmeonContextService,
    private taskProcessService: TaskProcessService,
    private taskProcessUpdatesService: TaskProcessUpdatesService,
    private taskProcessModalsService: TaskProcessModalsService,
    private angularMigrationHelperService: AngularMigrationHelperService,
    private ajsTriageService: AJSTriageService,
    private alcmeonContextService: AlcmeonContextService,
    private taskService: TaskService
  ) {}

  ngOnInit() {
    this.taskId = taskIdFromTaskOrActionId(this.task.id);
    this.isProcessedTask = this.taskProcessContext.isProcessedTask(this.task.id);
    this.isCRMMode = this.taskProcessContext.isCRMMode;

    this.taskActionsService.getTaskActions(this.taskId).subscribe(response => this.initWithTaskActions(response));
    this.taskProcessUpdatesService.reloadedTask$
      .pipe(
        filter(task => task.id === this.task.id),
        takeUntil(this.ngOnDestroy$)
      )
      .subscribe(task => (this.task = task as Task));
    this.isUserReadOnly = this.alcmeonContextService.isLoggedUserReadOnlyForSite(this.taskProcessContext.site.id);
  }

  ngOnDestroy() {
    this.ngOnDestroy$.next();
    this.ngOnDestroy$.complete();
    if (!this.isProcessedTask && this.tagsContext) {
      this.tagsContext.destroy();
    }
  }

  onRouteClicked(event) {
    this.selectRoutingTag.open(event);
  }

  private initWithTaskActions(response: TaskActionsResponse) {
    this.actions = response.actions as TagAction[];
    this.processActions(this.actions);
    const receviedMessageTranslationKey = this.task.isPrivate
      ? 'm/task-process/task-actions-received-msg-log-entry/private'
      : 'm/task-process/task-actions-received-msg-log-entry/public';
    this.receivedMsgLogEntry = {
      from: response.from,
      datetime: response.detailedTask.datetime,
      translatedText: this.basicService.translate(receviedMessageTranslationKey),
    };

    // For the dates on the timeline and the message bubbles content in case of answer, annotation or help action
    this.taskProcessService.setUIProperties([this.receivedMsgLogEntry, ...this.actions] as AnyTaskOrActionType[], null);

    this.site = response.site;
    this.externalUrl = response.externalUrl;
    if (this.isProcessedTask) {
      // If we're watching the action log for the currently processed task, let's use the same tags context,
      // so that any changes made by the user in the modal's tag selectors are reflected on the task process page
      this.tagsContext = this.taskProcessContext.tagsContext;
    } else {
      this.tagsContext = new TagsContext(
        response.tagGroups,
        response.routingTagGroup,
        this.taskId,
        this.task.taskStateCode,
        null
      );
    }
    this.routingTagGroup = this.tagsContext.getRoutingTagGroup();
    this.isProcessThisMessageButtonDisabled = !this.alcmeonContext.isLoggedUserPartOfTeam(
      this.routingTagGroup?.selectedTagId
    );
  }

  isAnswerOrAnnotationOrHelpAction(action: TagAction) {
    return [
      TaskOrActionType.Answer,
      TaskOrActionType.Annotation,
      TaskOrActionType.HelpRequest,
      TaskOrActionType.HelpAnswer,
    ].includes(action.type);
  }

  shouldShowProcessThisMessageButton() {
    // A user in push mode was allowed to navigate until the processing task, let this one works another task within the conversation.
    return !this.isCRMMode && !this.isProcessedTask;
  }

  shouldShowRouteButton() {
    // hide in desktop mode for the processed task, as it would be redundant with the "route" button above the bubble
    return !this.isCRMMode && this.routingTagGroup && (!this.isProcessedTask || !environment.bundledWithAngularJSApp);
  }

  isProcessThisTaskInNewTab() {
    return environment.bundledWithAngularJSApp;
  }

  async maybeConfirmThenProcessThisTask() {
    if (this.isTaskAssignedToOtherUser()) {
      const modalResult = await this.basicService.popoverWithResult(null, ConfirmStealComponent, {
        adminUser: this.task.adminUser,
      });
      if (modalResult.role === ModalDismissRoleEnum.OK) {
        this.processThisTask();
      }
    } else {
      this.processThisTask();
    }
  }

  private processThisTask() {
    if (environment.bundledWithAngularJSApp) {
      const companyId = this.alcmeonContext.companyId;
      const openTaskInNewTab = () => {
        const processThisTaskUrl = this.angularMigrationHelperService.processTaskURL(companyId, this.taskId);
        this.basicService.window.open(processThisTaskUrl, '_blank');
      };
      if (this.taskProcessContext.isPushboxMode) {
        this.basicService.showSpinner();
        // Assign the task to the user before to continue and allow the user to pass the guard.
        this.taskService
          .aquireTaskFromAnyState(this.taskId)
          .pipe(finalize(() => this.basicService.hideSpinner()))
          .subscribe(openTaskInNewTab);
      } else {
        openTaskInNewTab();
      }
    } else {
      this.taskProcessService.reloadTaskProcessStateWithNewTask(this.task.taskStateCode, this.taskId, this.isCRMMode);
      this.taskProcessModalsService.dismissTaskProcessPageModal();
    }
  }

  processThisTaskLabelKey() {
    return this.isTaskAssignedToOtherUser()
      ? 'm/task-process/task-actions-log/steal-this-message'
      : 'm/task-process/task-actions-log/process-this-message';
  }

  private isTaskAssignedToOtherUser() {
    return this.task.taskStateCode === TaskState.Assigned && !this.alcmeonContext.isMe(this.task.adminUser);
  }

  private processActions(actions: TagAction[]) {
    for (const action of actions) {
      const { type, adminUser } = action;
      const isActionByMe = this.alcmeonContext.isMe(adminUser);
      const keyPrefix = isActionByMe ? `m/action-in-audit-log/me/${type}` : `m/action-in-audit-log/name/${type}`;

      switch (type) {
        case TaskOrActionType.Tag:
          this.processTagAction(action, keyPrefix);
          break;
        case TaskOrActionType.AssignTo:
        case TaskOrActionType.Steal:
          this.processAssignToOrStealAction(action, keyPrefix);
          break;
        case TaskOrActionType.SendConversation:
          this.processSendConversationAction(action, keyPrefix);
          break;

        default:
          action.translatedText = this.basicService.translate(keyPrefix, { name: action.adminUser.name });
          break;
      }
      if (action.cause) {
        action.extraTranslatedText = this.translateActionCause(action);
      }
      this.maybeAddModifiedTask(action);
    }
  }

  private translateActionCause(action: TagAction) {
    let suffix = '';
    const { rule_id, code, title, handover_protocol } = action.cause;
    if (rule_id) {
      suffix = ` (${this.basicService.translate('triage_config/columns/rule_name')} : `;
      if (code) {
        // non editable rules
        suffix += `${this.basicService.translate('triage_config/default_rules/' + code)})`;
      } else if (title) {
        // custom rules
        suffix += `${title})`;
      } else {
        suffix = ` (${this.basicService.translate('action-in-audit-log/cause/rule-no-longer-exists')})`;
      }
    } else if (handover_protocol) {
      suffix = ` (${this.basicService.translate('action-in-audit-log/cause/handover-protocol-alcmeon-not-in-charge')})`;
    }
    return suffix;
  }

  shouldDisplayTextWithLinkToRule(action) {
    return (
      this.alcmeonContext.isLoggedUserAdmin() &&
      action.cause &&
      (action.cause.title || action.cause.code) &&
      environment.bundledWithAngularJSApp
    );
  }

  goToTriageRule(action) {
    if (action && action.cause.rule_id) {
      this.ajsTriageService.goToTriageRule(action.cause.rule_id);
    }
  }

  // for actions of type 'modified' (when a social network user has modified their Trustpilot review for example),
  // we display a mesasge bubble in the action log, with the updated text
  private maybeAddModifiedTask(action) {
    if (action.type === TaskOrActionType.Modified) {
      const modifiedAction = action as ModifiedAction;
      modifiedAction.updatedTask = {
        ...this.task, // shallow-clone the original task
        content: {
          text: modifiedAction.newContent,
          attachments: [],
        },
        datetime: modifiedAction.datetime,
      };
    }
  }

  private processAssignToOrStealAction(action, keyPrefix: string) {
    const key = this.alcmeonContext.isMe(action.targetAdminUser) ? `${keyPrefix}/me` : `${keyPrefix}/name`;
    const translationParams = {
      // Depending on the translation key, only some of the params below will be used
      name: action.adminUser.name,
      previous_user: action.targetAdminUser.name,
      new_user: action.targetAdminUser.name,
    };
    action.translatedText = this.basicService.translate(key, translationParams);
  }

  private processTagAction(action: TagAction, keyPrefix: string) {
    const key = action.isRouting ? `${keyPrefix}/routing` : keyPrefix;
    const translationParams = {
      // Depending on the translation key, only some of the params below will be used
      name: action.adminUser.name,
      team: action.tagName,
      tag: action.tagName,
      group: action.tagGroupName,
    };
    action.translatedText = this.basicService.translate(key, translationParams);
  }

  private processSendConversationAction(action: TagAction, keyPrefix: string) {
    const { adminUser, recipients } = action as any;
    const { name } = adminUser;
    action.translatedText = this.basicService.translate(keyPrefix, { name, recipients });
  }
}
